import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { Search, UserX } from 'lucide-react'
import { toast } from 'sonner'
import { LoadError } from '@/components/campaigns/LoadError'
import { formatDay } from '@/components/campaigns/campaignFormat'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SeccionPlegable } from '@/components/ui/seccion-plegable'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import {
  useAgentCensusQuery,
  useExcludeAgent,
  useExcludedAgentsQuery,
  useReadmitAgent,
} from '@/queries'
import type { AgentExclusion } from '@/services/visitSchedulingService'

interface AgentRow {
  id: string
  name: string
  exclusion?: AgentExclusion
}

/**
 * Qué comerciales no reciben visitas de MaRK ni leads de portal.
 *
 * Sustituye a quitarle el calendario, que no duraba: la sincronización del censo
 * vuelve a activarlo. La lista sale del censo más los ya excluidos, por si alguno
 * ha dejado de estar en el censo y hay que poder sacarlo de aquí.
 */
export function ExcludedAgentsCard() {
  const { t } = useTranslation()
  return (
    <SeccionPlegable
      numero="08"
      etiqueta={t('excluded_agents.label')}
      titulo={t('excluded_agents.title')}
      descripcion={t('excluded_agents.description')}
    >
      <ExcludedAgentsList />
    </SeccionPlegable>
  )
}

function ExcludedAgentsList() {
  const { t } = useTranslation()
  const { lang = 'es' } = useParams()
  const census = useAgentCensusQuery()
  const exclusions = useExcludedAgentsQuery()
  const exclude = useExcludeAgent()
  const readmit = useReadmitAgent()
  const [search, setSearch] = useState('')
  const [reasons, setReasons] = useState<Record<string, string>>({})
  const [optimistic, setOptimistic] = useState<Record<string, boolean>>({})

  const rows = useMemo<AgentRow[]>(() => {
    const byId = new Map<string, AgentRow>()
    for (const binding of census.data ?? []) {
      // Un comercial puede tener varios móviles. Si alguna fila trae nombre, vale esa.
      const existing = byId.get(binding.id_comercial)
      if (!existing || !existing.name) {
        byId.set(binding.id_comercial, { id: binding.id_comercial, name: binding.display_name })
      }
    }
    for (const exclusion of exclusions.data ?? []) {
      const existing = byId.get(exclusion.id_gestor)
      byId.set(exclusion.id_gestor, {
        id: exclusion.id_gestor,
        name: existing?.name || exclusion.nombre,
        exclusion,
      })
    }
    const needle = search.trim().toLowerCase()
    return [...byId.values()]
      .filter((row) => !needle || row.name.toLowerCase().includes(needle) || row.id.includes(needle))
      .sort(
        (a, b) =>
          Number(Boolean(b.exclusion)) - Number(Boolean(a.exclusion)) || a.name.localeCompare(b.name, lang),
      )
  }, [census.data, exclusions.data, search, lang])

  const toggle = (row: AgentRow, excluded: boolean) => {
    const label = row.name || row.id
    const settle = () =>
      setOptimistic((prev) => {
        const next = { ...prev }
        delete next[row.id]
        return next
      })
    setOptimistic((prev) => ({ ...prev, [row.id]: excluded }))
    const onError = (error: Error) => {
      settle()
      toast.error(error.message)
    }
    if (excluded) {
      exclude.mutate(
        { id_gestor: row.id, nombre: row.name, motivo: (reasons[row.id] ?? '').trim() },
        {
          onSuccess: () => {
            settle()
            toast.success(t('excluded_agents.excluded', { name: label }))
          },
          onError,
        },
      )
    } else {
      readmit.mutate(row.id, {
        onSuccess: () => {
          settle()
          toast.success(t('excluded_agents.readmitted', { name: label }))
        },
        onError,
      })
    }
  }

  if (census.isPending || exclusions.isPending) {
    return (
      <div className="space-y-2" data-testid="excluded-loading">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }
  // Sin la lista de excluidos no se puede decir quién lo está: mejor no enseñar nada.
  if (exclusions.isError) {
    return <LoadError error={exclusions.error} onRetry={() => void exclusions.refetch()} />
  }

  return (
    <div className="min-w-0 space-y-4">
      {census.isError ? <LoadError error={census.error} onRetry={() => void census.refetch()} /> : null}

      <div className="relative max-w-sm">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          className="pl-8"
          type="search"
          value={search}
          aria-label={t('excluded_agents.search')}
          placeholder={t('excluded_agents.search')}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          icon={<UserX className="h-6 w-6" />}
          title={search.trim() ? t('excluded_agents.empty_search') : t('excluded_agents.empty_title')}
          description={search.trim() ? undefined : t('excluded_agents.empty_description')}
        />
      ) : (
        <ul className="divide-y">
          {rows.map((row) => {
            const excluded = optimistic[row.id] ?? Boolean(row.exclusion)
            const switchId = `excluded-${row.id}`
            return (
              <li key={row.id} className="flex flex-wrap items-center gap-4 py-2">
                <div className="min-w-48 flex-1 space-y-1">
                  <Label htmlFor={switchId}>{row.name || t('excluded_agents.no_name')}</Label>
                  <p className="font-mono text-xs text-muted-foreground">{row.id}</p>
                </div>
                {row.exclusion ? (
                  <p className="min-w-48 flex-1 text-xs text-muted-foreground">
                    {row.exclusion.motivo ? `${row.exclusion.motivo} · ` : ''}
                    {t('excluded_agents.excluded_by', {
                      who: row.exclusion.created_by || '—',
                      date: formatDay(row.exclusion.created_at, lang),
                    })}
                  </p>
                ) : (
                  <Input
                    className="min-w-48 flex-1"
                    maxLength={300}
                    aria-label={t('excluded_agents.reason_for', { name: row.name || row.id })}
                    placeholder={t('excluded_agents.reason_placeholder')}
                    value={reasons[row.id] ?? ''}
                    onChange={(event) => setReasons((prev) => ({ ...prev, [row.id]: event.target.value }))}
                  />
                )}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{t('excluded_agents.switch_label')}</span>
                  <Switch
                    id={switchId}
                    checked={excluded}
                    aria-label={t('excluded_agents.switch_for', { name: row.name || row.id })}
                    disabled={optimistic[row.id] !== undefined}
                    onCheckedChange={(checked) => toggle(row, checked)}
                  />
                </div>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
